/**
 * "View for user" — render a model run's output the way the student sees it.
 *
 * A parsed-JSON tree tells you the run succeeded; it doesn't tell you whether
 * the report reads well, whether a verdict is calibrated, or whether a focus
 * area is actually actionable. This renders the run's output through the SAME
 * presentational component the student app uses, so those are visible.
 *
 * Registry-keyed on prompt type: add an entry to render another prompt's
 * student view. Prompts with no entry render nothing (no empty card).
 *
 * Renders THIS RUN's output. The server's finalize step then validates evidence
 * against the session, caps the lists and derives practice deep-links, so a
 * shipped report can differ slightly — the note in the header says so rather
 * than implying byte-equality.
 */

import type { ReactNode } from 'react';
import { QuestionSessionReport } from '@shared-ui/question-session-report';
import type { QuestionSessionReportData } from '@shared-ui/question-session-report';
import type { PromptType } from '@padcanvas/shared';
import './StudentViewSection.css';

/** The LLM's raw report shape, before the server's finalize pass. */
interface RawReport {
    verdict?: string;
    trajectoryCaption?: string;
    strengths?: unknown[];
    focusAreas?: unknown[];
    nextStep?: string;
}

/**
 * Coerce a raw LLM report into the shape the student component renders.
 * Deliberately permissive: this is a debugging view, so a partial or slightly
 * malformed payload should still render what it can rather than blank out.
 */
function toReportData(raw: RawReport): QuestionSessionReportData {
    const points = (arr: unknown[] | undefined) =>
        (Array.isArray(arr) ? arr : []).map((p) => {
            const o = (p ?? {}) as Record<string, unknown>;
            return {
                title: String(o.title ?? ''),
                body: String(o.body ?? ''),
                subject: typeof o.subject === 'string' ? o.subject : undefined,
                trend: (o.trend as QuestionSessionReportData['strengths'][number]['trend']) ?? 'new',
                evidence: Array.isArray(o.evidence) ? o.evidence.map(String) : [],
                ...(typeof o.impact === 'string' ? { impact: o.impact } : {}),
                ...(typeof o.recurrenceCount === 'number' ? { recurrenceCount: o.recurrenceCount } : {}),
            };
        });

    return {
        status: 'ready',
        verdict: String(raw.verdict ?? ''),
        // The series is server-supplied and isn't part of the LLM's output, so
        // there is nothing to sparkline here — only the caption it wrote.
        trajectory: raw.trajectoryCaption?.trim()
            ? { series: [], caption: raw.trajectoryCaption.trim() }
            : undefined,
        strengths: points(raw.strengths) as QuestionSessionReportData['strengths'],
        focusAreas: points(raw.focusAreas) as QuestionSessionReportData['focusAreas'],
        nextStep: String(raw.nextStep ?? ''),
    } as QuestionSessionReportData;
}

/** promptType → how to render that run's output as the student saw it. */
const STUDENT_VIEWS: Partial<Record<PromptType, (parsed: Record<string, unknown>) => ReactNode>> = {
    generate_question_session_report: (parsed) => (
        <QuestionSessionReport report={toReportData(parsed as RawReport)} />
    ),
};

interface Props {
    promptType?: string;
    /** The run's raw response text (JSON for structured prompts). */
    rawResponse?: string | null;
}

export function StudentViewSection({ promptType, rawResponse }: Props) {
    const render = promptType ? STUDENT_VIEWS[promptType as PromptType] : undefined;
    if (!render) return null;

    let parsed: Record<string, unknown> | null = null;
    let parseError: string | null = null;
    try {
        // Providers sometimes fence JSON despite instructions; strip it rather
        // than showing a parse error for output that is otherwise fine.
        const text = (rawResponse ?? '').trim().replace(/^```(?:json)?\s*|\s*```$/g, '');
        parsed = text ? JSON.parse(text) as Record<string, unknown> : null;
    } catch (e) {
        parseError = e instanceof Error ? e.message : 'could not parse the response as JSON';
    }

    return (
        <div className="card">
            <h3>View for user</h3>
            <p className="student-view-note">
                This run&apos;s output rendered with the student&apos;s own component. The server&apos;s
                finalize step validates evidence, caps the lists and adds practice links, so a shipped
                report can differ slightly.
            </p>
            {parseError ? (
                <p className="student-view-empty">Couldn&apos;t render: {parseError}</p>
            ) : !parsed ? (
                <p className="student-view-empty">This run produced no output to render.</p>
            ) : (
                <div className="student-view-stage">{render(parsed)}</div>
            )}
        </div>
    );
}
